import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Facebook, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactInfo } from '../data/mock';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    requirement: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Thank you for reaching out!",
      description: "Our team will contact you shortly with the best opportunities.",
    });

    // Reset form
    setFormData({
      name: '',
      phone: '',
      requirement: ''
    });
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-gradient-to-b from-white to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A1F3D] mb-4">
            Get In <span className="text-[#C9A14A]">Touch</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Looking to invest or promote your venture? Let's connect.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-[#0A1F3D] mb-6">
                Contact Information
              </h3>
              <p className="text-gray-600 text-lg mb-8">
                Reach out to us for premium property solutions and investment opportunities.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              {/* Phone Numbers */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#C9A14A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#C9A14A]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0A1F3D] mb-2">Phone</h4>
                  <div className="space-y-1">
                    {contactInfo.phones.map((phone, idx) => (
                      <a
                        key={idx}
                        href={`tel:${phone}`}
                        className="block text-gray-600 hover:text-[#C9A14A] transition-colors duration-300"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#C9A14A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#C9A14A]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0A1F3D] mb-2">Email</h4>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-gray-600 hover:text-[#C9A14A] transition-colors duration-300"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#C9A14A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#C9A14A]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0A1F3D] mb-2">Location</h4>
                  <p className="text-gray-600 leading-relaxed">{contactInfo.address}</p>
                </div>
              </div>
            </div>

            {/* View on Google Maps Button */}
            <div className="mt-8">
              <a
                href={contactInfo.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  className="w-full bg-[#C9A14A] hover:bg-[#B89140] text-[#0A1F3D] font-bold text-base sm:text-lg py-5 sm:py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  View on Google Maps
                </Button>
              </a>
            </div>

            {/* Social Media Links */}
            <div className="mt-8">
              <h4 className="font-semibold text-[#0A1F3D] mb-4 text-center">Follow Us</h4>
              <div className="flex justify-center space-x-4">
                <a
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#C9A14A]/20 hover:bg-[#C9A14A] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                  aria-label="Facebook"
                >
                  <Facebook className="w-6 h-6 text-[#C9A14A] group-hover:text-[#0A1F3D] transition-colors duration-300" />
                </a>
                <a
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#C9A14A]/20 hover:bg-[#C9A14A] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6 text-[#C9A14A] group-hover:text-[#0A1F3D] transition-colors duration-300" />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-[#0A1F3D] mb-6">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="requirement" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Requirement
                </label>
                <Textarea
                  id="requirement"
                  name="requirement"
                  required
                  value={formData.requirement}
                  onChange={handleChange}
                  placeholder="Tell us about your property requirements or investment plans"
                  rows={4}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#C9A14A] hover:bg-[#B89140] text-[#0A1F3D] font-bold text-base sm:text-lg py-5 sm:py-6 rounded-xl"
              >
                <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Submit Inquiry
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
